// frontend/src/App.js

import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import md3Theme from './theme/md3Theme';

import Home from './pages/Home';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import PageTracker from './components/PageTracker';
import Footer from './components/Layout/Footer';
import { initGA } from './utils/analytics';
import './index.css';

// Lazy-loaded components and pages for optimal bundle splitting
const BobChatWidget = lazy(() => import('./components/BobChatWidget'));
const ConsentBanner = lazy(() => import('./components/ConsentBanner'));
const FeedbackBanner = lazy(() => import('./components/FeedbackBanner'));
const GroupsPage = lazy(() => import('./pages/GroupsPage'));
const GroupDetailPage = lazy(() => import('./pages/GroupDetailPage'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const Impressum = lazy(() => import('./pages/Impressum'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'));
const EditBlogPost = lazy(() => import('./pages/EditBlogPost'));
const EmailVerified = lazy(() => import('./pages/EmailVerified'));
const VerifyRegistration = lazy(() => import('./pages/VerifyRegistration'));
const SeminararbeitSurvey = lazy(() => import('./pages/SeminararbeitSurvey'));
const TeamMemberDetail = lazy(() => import('./pages/TeamMemberDetail'));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const EventRegistrationPage = lazy(() => import('./pages/EventRegistrationPage'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminEvents = lazy(() => import('./pages/AdminEvents'));
const SetupProfile = lazy(() => import('./pages/SetupProfile'));
const EditAvatar = lazy(() => import('./pages/EditAvatar'));
const UserDetailPage = lazy(() => import('./pages/UserDetailPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center p-8">
    <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

function ConditionalFooter() {
  const location = useLocation();
  if (location.pathname.startsWith('/dashboard')) {
    return null;
  }
  return <Footer />;
}

function ConditionalBob() {
  const location = useLocation();
  const { settings } = useSettings();
  if (location.pathname.startsWith('/dashboard')) {
    return null;
  }
  const isAiDisabled =
    settings?.features?.bobChat === false ||
    settings?.features?.aiAssistant === false ||
    settings?.modules?.aiAssistant === false ||
    settings?.ai?.enabled === false ||
    settings?.ai_settings?.enabled === false;

  if (isAiDisabled) {
    return null;
  }
  return (
    <Suspense fallback={null}>
      <BobChatWidget />
    </Suspense>
  );
}

function ConditionalConsent() {
  const { settings } = useSettings();
  if (settings?.features?.cookieConsent === false) {
    return null;
  }
  return (
    <Suspense fallback={null}>
      <ConsentBanner />
    </Suspense>
  );
}

const RedirectToDashboard = () => {
  const dashboardUrl = process.env.REACT_APP_DASHBOARD_URL || `${window.location.origin}/dashboard`;
  const path = window.location.pathname.replace(/^\/dashboard/, '');
  window.location.href = `${dashboardUrl}${path}${window.location.search}`;
  return null;
};

function App() {
  useEffect(() => {
    initGA();
  }, []);

  return (
    <SettingsProvider>
      <ThemeProvider theme={md3Theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <div id="root-container" className="flex flex-col min-h-screen">
              <PageTracker />
              <main className="flex-grow min-h-screen">
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/gruppen" element={<GroupsPage />} />
                    <Route path="/gruppen/:slugOrId" element={<GroupDetailPage />} />
                    <Route path="/gruppe/:slugOrId" element={<GroupDetailPage />} />
                    <Route path="/stufen" element={<GroupsPage />} />
                    <Route path="/stufen/:slugOrId" element={<GroupDetailPage />} />
                    <Route path="/blog" element={<Navigate to="/#blog-section" replace />} />
                    <Route path="/blog/:id" element={<BlogDetail />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgotpassword" element={<ForgotPassword />} />
                    <Route path="/resetpassword/:resettoken" element={<ResetPassword />} />
                    <Route path="/email-verified" element={<EmailVerified />} />
                    <Route path="/verify-registration" element={<VerifyRegistration />} />
                    <Route path="/google-callback" element={<GoogleCallback />} />
                    <Route path="/dashboard/*" element={<RedirectToDashboard />} />
                    <Route path="/impressum" element={<Impressum />} />
                    <Route path="/surveys/seminararbeit" element={<SeminararbeitSurvey />} />
                    <Route path="/umfrage" element={<SeminararbeitSurvey />} />
                    <Route path="/edit-blog/:id" element={<PrivateRoute><EditBlogPost /></PrivateRoute>} />
                    <Route path="/setup-profile" element={<PrivateRoute><SetupProfile /></PrivateRoute>} />
                    <Route path="/edit-avatar" element={<PrivateRoute><EditAvatar /></PrivateRoute>} />

                    <Route path="/team/:id" element={<TeamMemberDetail />} />
                    <Route path="/t/:id" element={<TeamMemberDetail />} />

                    <Route path="/events/:slug/register" element={<EventRegistrationPage />} />
                    <Route path="/events/:slug" element={<EventDetailPage />} />
                    <Route path="/admin/users" element={<PrivateRoute><AdminUsers /></PrivateRoute>} />
                    <Route path="/admin/events" element={<PrivateRoute><AdminEvents /></PrivateRoute>} />
                    <Route path="/profile" element={<PrivateRoute><UserDetailPage /></PrivateRoute>} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </main>

              {/* Footer */}
              <ConditionalFooter />
            </div> {/* Ende von #root-container */}
            <ConditionalBob />
            <ConditionalConsent />
            <Suspense fallback={null}>
              <FeedbackBanner />
            </Suspense>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </SettingsProvider>
  );
}

export default App;
